import type { Immutable } from 'immer';
import React, { useMemo, useState } from 'react';
import type { DayIndex } from '../day-part/utils';
import {
  getResolvedThreshold,
  type NowForDayPart,
  type ResolvedThresholdType,
} from '../layout/getResolvedThreshold';
import { type ServiceDisplayWidget } from '../layout/types';
import { useServiceDisplayDayParts } from './useServiceDisplayDayParts';

const INTERVAL_MS = 60_000;

function getNowForDayPart(): NowForDayPart {
  const d = new Date();
  return {
    dayIndex: String(d.getDay()) as DayIndex,
    minutesSinceMidnight: d.getHours() * 60 + d.getMinutes(),
  };
}

export interface ResolvedThresholdResult {
  value: number;
  isFallback: boolean;
}

/**
 * Returns the threshold to use for level (warning/critical) based on the current time and
 * the widget's day-part thresholds. Updates at most every 60s when day-part thresholds exist.
 */
export function useResolvedThreshold(
  widget: Immutable<ServiceDisplayWidget>,
  fallback: number,
  type: ResolvedThresholdType,
): ResolvedThresholdResult {
  const hasDayPartThresholds = (widget.thresholds?.length ?? 0) > 0;
  const [now, setNow] = useState(getNowForDayPart);

  React.useEffect(() => {
    if (!hasDayPartThresholds) return;
    const interval = setInterval(() => setNow(getNowForDayPart()), INTERVAL_MS);
    return () => clearInterval(interval);
  }, [hasDayPartThresholds]);

  const { data: dayParts } = useServiceDisplayDayParts();

  return useMemo(() => {
    if (!hasDayPartThresholds) return { value: fallback, isFallback: true };
    const value = getResolvedThreshold(
      widget.thresholds ?? [],
      dayParts ?? [],
      now,
      fallback,
      type,
    );
    return { value, isFallback: value === fallback };
  }, [hasDayPartThresholds, widget.thresholds, dayParts, now, fallback, type]);
}
